export interface TimeSeriesData {
  timestamp: string;
  [key: string]: any;  // Allow dynamic metrics
}

export interface StationData {
  id: string;
  name: string;
  description?: string;
  timeseries: TimeSeriesData[];
  period_hours: number;
  data_points: number;
}

/**
 * Convert timeseries data to CSV format
 * Dynamically includes all available metrics from the data
 */
export const convertToCSV = (stationData: StationData): string => {
  if (stationData.timeseries.length === 0) {
    return '# No data available\n';
  }

  // Collect all unique metric keys (excluding timestamp)
  const allKeys = new Set<string>();
  stationData.timeseries.forEach(row => {
    Object.keys(row).forEach(key => {
      if (key !== 'timestamp') {
        allKeys.add(key);
      }
    });
  });

  const metricKeys = Array.from(allKeys).sort();

  // Create header mapping with units
  const headerMapping: Record<string, string> = {
    temperature_celsius: 'Temperature (°C)',
    humidity: 'Humidity (%)',
    atmospheric_pressure_mbar: 'Pressure (mbar)',
    wind_speed_m_per_s: 'Wind Speed (m/s)',
    wind_direction_deg: 'Wind Direction (°)',
    rain_mm: 'Rain (mm)',
    solar_radiation_watt_per_sqm: 'Solar Radiation (W/m²)',
    reference_et_mm: 'Reference ET (mm)'
  };

  // Build headers
  const headers = ['Timestamp', ...metricKeys.map(key => headerMapping[key] || key)];

  // Create CSV header
  let csv = headers.join(',') + '\n';

  // Add metadata as comments
  csv += `# Station: ${stationData.name}\n`;
  csv += `# Description: ${stationData.description || 'N/A'}\n`;
  csv += `# Station ID: ${stationData.id}\n`;
  csv += `# Period: ${stationData.period_hours} hours\n`;
  csv += `# Data Points: ${stationData.data_points}\n`;
  csv += `# Metrics: ${metricKeys.join(', ')}\n`;
  csv += `# Exported: ${new Date().toISOString()}\n`;
  csv += '#\n';

  // Add data rows
  stationData.timeseries.forEach(row => {
    const csvRow = [
      row.timestamp || '',
      ...metricKeys.map(key => {
        const value = row[key];
        return value !== null && value !== undefined ? value.toString() : '';
      })
    ];
    csv += csvRow.join(',') + '\n';
  });

  return csv;
};

/**
 * Download CSV data as a file
 */
export const downloadCSV = (csvData: string, filename: string) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Generate filename for CSV export
 */
export const generateCSVFilename = (stationName: string, stationId: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
  const sanitizedName = stationName.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `station_${sanitizedName}_${stationId}_${timestamp}.csv`;
};
